import React, { useState, useEffect } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Switch, IconButton, Tooltip, Box, Typography, Slider } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useSearchSettings } from '../../contexts/SearchSettingsContext';
import { useHelpContent } from '../../utils/HelpUtils';

interface SettingsMenuProps {
  onAutoPasteChange: (enabled: boolean) => void;
  fontSize: number;
  onFontSizeChange: (size: number) => void;
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({ onAutoPasteChange, fontSize, onFontSizeChange }) => {
  const { settings, updateSettings } = useSearchSettings();
  const { getToolbarTooltipTextForState } = useHelpContent();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [autoPasteEnabled, setAutoPasteEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('autoPasteEnabled');
    return saved ? JSON.parse(saved) : false;
  });
  const [tooltipFontSize, setTooltipFontSize] = useState<number>(() => {
    const saved = localStorage.getItem('tooltipFontSize');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        // If parsing fails, remove the invalid value and use default
        localStorage.removeItem('tooltipFontSize');
        return 16;
      }
    }
    return 16;
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAutoPasteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setAutoPasteEnabled(newValue);
    localStorage.setItem('autoPasteEnabled', JSON.stringify(newValue));
    onAutoPasteChange(newValue);
  };

  const handleHelpModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    updateSettings({ ...settings, helpMode: newValue });
  };

  const handleShortTooltipsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    updateSettings({ ...settings, shortTooltips: newValue });
  };

  const handleTooltipFontSizeChange = (event: Event, newValue: number | number[]) => {
    const fontSize = newValue as number;
    setTooltipFontSize(fontSize);
    localStorage.setItem('tooltipFontSize', JSON.stringify(fontSize));
    // Update CSS custom property for tooltips
    document.documentElement.style.setProperty('--tooltip-font-size', `${fontSize}px`);
  };

  // Initialize tooltip font size on component mount
  useEffect(() => {
    document.documentElement.style.setProperty('--tooltip-font-size', `${tooltipFontSize}px`);
  }, [tooltipFontSize]);

  // Initialize tooltip font size on app startup
  useEffect(() => {
    const savedFontSize = localStorage.getItem('tooltipFontSize');
    if (savedFontSize) {
      try {
        const fontSize = JSON.parse(savedFontSize);
        document.documentElement.style.setProperty('--tooltip-font-size', `${fontSize}px`);
      } catch (error) {
        // If parsing fails, remove the invalid value and use default
        localStorage.removeItem('tooltipFontSize');
        document.documentElement.style.setProperty('--tooltip-font-size', '16px');
      }
    } else {
      // Set default if no saved value
      document.documentElement.style.setProperty('--tooltip-font-size', '16px');
    }
  }, []);

  return (
    <>
      <Tooltip title={getToolbarTooltipTextForState('settings', true)} placement="left">
        <IconButton
          color="inherit"
          onClick={handleClick}
          size="small"
          sx={{ ml: 2 }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Tooltip title={getToolbarTooltipTextForState('auto-paste', true)} placement="left">
          <span>
            <MenuItem>
              <ListItemText primary="Auto-paste from clipboard" />
              <Switch
                edge="end"
                checked={autoPasteEnabled}
                onChange={handleAutoPasteChange}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('help-mode', true)} placement="left">
          <span>
            <MenuItem>
              <ListItemText primary="Detailed help tooltips" />
              <Switch
                edge="end"
                checked={settings.helpMode}
                onChange={handleHelpModeChange}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('short-tooltips', !settings.helpMode)} placement="left">
          <span>
            <MenuItem>
              <ListItemText primary="Short tooltips" />
              <Switch
                edge="end"
                checked={settings.shortTooltips}
                onChange={handleShortTooltipsChange}
                disabled={settings.helpMode}
              />
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getToolbarTooltipTextForState('tooltip-font-size', true)} placement="left">
          <span>
            <MenuItem sx={{ flexDirection: 'column', alignItems: 'stretch', py: 2 }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Tooltip Font Size: {tooltipFontSize}px
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    A
                  </Typography>
                  <Slider
                    value={tooltipFontSize}
                    onChange={handleTooltipFontSizeChange}
                    min={12}
                    max={24}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ mx: 1, flex: 1 }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '24px', color: 'text.secondary' }}>
                    A
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title="Application Font Size" placement="left">
          <span>
            <MenuItem sx={{ flexDirection: 'column', alignItems: 'stretch', py: 2 }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Application Font Size: {fontSize}px
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    A
                  </Typography>
                  <Slider
                    value={fontSize}
                    onChange={(event, newValue) => onFontSizeChange(newValue as number)}
                    min={10}
                    max={24}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ mx: 1, flex: 1 }}
                  />
                  <Typography variant="body2" sx={{ fontSize: '24px', color: 'text.secondary' }}>
                    A
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
};

export default SettingsMenu; 